/**
 * Parámetros de la MEDICIÓN de planos DWG/DXF (lector `infra/dxf/plano2svg.py` de OPUS ci).
 *
 * Son tolerancias y rangos de plausibilidad, así que NO viven fijos en el código: se editan en la
 * calibración del motor (`motor_calibracion.calibracion.umbrales.dwg_*`, panel staff de Calibración).
 * El worker los lee de la calibración vigente y se los pasa al script en un JSON (`--params`); el
 * script no trae ningún default propio. Esto es el DEFAULT del seed y el respaldo si la calibración
 * trae un valor inválido.
 *
 * Todas las distancias van en METROS (sufijo `_m`) y el script las pasa a unidades del dibujo con el
 * factor de escala que resolvió: así la misma casa dibujada en m, cm o mm mide lo mismo (antes las
 * tolerancias estaban en unidades del dibujo y la casa en mm medía el muro al doble).
 * Calibrados contra el banco de la vara DWG (`infra/dxf/vara/`, `pnpm test:vara-dwg`) y cs_05.
 */
export interface ParametrosMedicionDwg {
  // --- Paso 1: tolerancias del lector, en metros ---------------------------------------------------
  /** Resolución de coordenadas: los vértices se redondean a esta distancia antes de medir. */
  dwg_resolucion_m: number;
  /** Aplanado de curvas: flecha máxima entre el arco y la cuerda que lo reemplaza. */
  dwg_aplanado_m: number;
  /** Muro de doble trazo: separación mínima entre caras para contar como espesor (menos = líneas repetidas). */
  dwg_muro_espesor_min_m: number;
  /** Muro de doble trazo: separación máxima entre dos caras para emparejarlas como un muro. */
  dwg_muro_espesor_max_m: number;
  /** Muro de doble trazo: ancho de la clase con que se agrupan las separaciones para hallar el espesor dominante. */
  dwg_muro_espesor_paso_m: number;
  /** Mínimo de pares de caras para tratar una capa como muro de doble trazo (y para que el espesor opine en la escala). */
  dwg_muro_pares_min: number;
  /** Nitidez mínima del doble trazo (0–1: fracción de pares en el espesor dominante) para que opine en la escala y para no tomar una superficie por muro. */
  dwg_muro_conf_min: number;
  /** Recintos: tolerancia de cierre mínima (funde extremos casi coincidentes). */
  dwg_snap_min_m: number;
  /** Recintos: tolerancia de cierre máxima. */
  dwg_snap_max_m: number;
  /** Recintos: hueco mínimo entre dos tramos de muro colineales para tratarlo como vano. */
  dwg_hueco_min_m: number;
  /** Recintos: vano más ancho que se puentea sin bajar el recinto a 'aproximado'. */
  dwg_vano_fino_max_m: number;
  // --- Paso 2: escala. Rangos de lo que es una OBRA plausible, en metros ----------------------------
  /** Lado mayor del conjunto dibujado: por debajo, la unidad candidata da un dibujo imposible de tan chico. */
  dwg_extension_min_m: number;
  /** Lado mayor del conjunto dibujado: por encima, imposible de tan grande. */
  dwg_extension_max_m: number;
  /** Cota típica (mediana de las cotas lineales): mínima plausible. */
  dwg_cota_tipica_min_m: number;
  /** Cota típica: máxima plausible. */
  dwg_cota_tipica_max_m: number;
  /** Ancho de una puerta (radio del abatimiento de un bloque con hoja + arco), mínimo plausible. */
  dwg_puerta_ancho_min_m: number;
  /** Ancho de una puerta, máximo plausible. */
  dwg_puerta_ancho_max_m: number;
  /** Tolerancia relativa para reconocer un abatimiento: hoja con su bisagra en el centro del arco y largo = radio. */
  dwg_puerta_tolerancia: number;
  /** Tolerancia relativa para decir que el número rotulado de una cota y su geometría difieren en una potencia de 10. */
  dwg_rotulo_tolerancia: number;
  // --- Paso 7: vistas (láminas) dentro del archivo -------------------------------------------------
  /** Dos trazos más cerca que esto pertenecen a la misma vista (planta, elevación, corte). */
  dwg_vista_union_m: number;
  /** Una isla con menos entidades que esto (un símbolo suelto, un eje) se suma a la vista más cercana. */
  dwg_vista_entidades_min: number;
  // --- Paso 9: altura por planta, leída de elevaciones y cortes ---------------------------------------
  /** Altura de entrepiso mínima plausible: las cotas verticales más chicas son losas, firmes o pretiles. */
  dwg_altura_entrepiso_min_m: number;
  /** Altura de entrepiso máxima plausible: las más grandes son totales o dobles alturas. */
  dwg_altura_entrepiso_max_m: number;
  // --- Umbrales de forma del lector (antes RECINTO_* / CUANT_* fijos en el script) -------------------
  /** Nitidez del doble trazo (0–1) a partir de la cual el eje del muro es 'medida' (debajo, 'aproximada'). */
  dwg_muro_conf_alta: number;
  /**
   * Peso mínimo (0–1, fracción del largo emparejado de la capa) de una familia de espesores para que
   * sus pares cuenten como muro. Los pares de otras separaciones (muebles de doble línea, jambas) no
   * suman al eje: se reportan como `otros_pares`.
   */
  dwg_muro_familia_peso_min: number;
  /** Área encerrada / (eje × espesor) por encima de la cual una capa "de muro" poco nítida es una superficie. */
  dwg_superficie_ratio_max: number;
  /** Cobertura mínima (0–1) de los recintos reconstruidos para ofrecer su área aproximada. */
  dwg_recintos_cobertura_min: number;
  /** Recinto mínimo: lado en espesores de muro (descarta cajas de columna y ductos). */
  dwg_recinto_lado_min_esp: number;
  /** Compacidad (0–1) por debajo de la cual una cara cerrada es una tira, no un recinto. */
  dwg_recinto_compacidad_sliver: number;
  /** Compacidad (0–1) a partir de la cual la forma es de cuarto. */
  dwg_recinto_compacidad_fina: number;
  /** Fusión de nodos al cerrar recintos, en espesores de muro (colapsa las dos caras en un eje). */
  dwg_recinto_snap_esp: number;
  // --- Paso 8: cotas contra la geometría ------------------------------------------------------------
  /** Distancia máxima entre el punto de origen de una cota y un trazo para decir que la cota lo mide. */
  dwg_cota_asociacion_m: number;
  /** Diferencia máxima entre el número escrito en una cota y lo que mide su dibujo. */
  dwg_cota_discrepancia_m: number;
}

export const PARAMETROS_MEDICION_DWG_DEFAULT: Readonly<ParametrosMedicionDwg> = {
  dwg_resolucion_m: 0.001,
  dwg_aplanado_m: 0.005,
  dwg_muro_espesor_min_m: 0.03,
  dwg_muro_espesor_max_m: 1.0,
  dwg_muro_espesor_paso_m: 0.05,
  dwg_muro_pares_min: 5,
  dwg_muro_conf_min: 0.4,
  dwg_snap_min_m: 0.02,
  dwg_snap_max_m: 0.5,
  dwg_hueco_min_m: 0.05,
  dwg_vano_fino_max_m: 1.2,
  dwg_extension_min_m: 0.1,
  dwg_extension_max_m: 20000,
  dwg_cota_tipica_min_m: 0.01,
  dwg_cota_tipica_max_m: 2000,
  dwg_puerta_ancho_min_m: 0.4,
  dwg_puerta_ancho_max_m: 2.0,
  dwg_puerta_tolerancia: 0.05,
  dwg_rotulo_tolerancia: 0.02,
  dwg_vista_union_m: 0.3,
  dwg_vista_entidades_min: 10,
  dwg_altura_entrepiso_min_m: 2.0,
  dwg_altura_entrepiso_max_m: 6.0,
  // Mismos valores que tenían fijos en el script (RECINTO_* / CUANT_*): el comportamiento no cambia.
  dwg_muro_conf_alta: 0.6,
  dwg_muro_familia_peso_min: 0.1,
  dwg_superficie_ratio_max: 1.0,
  dwg_recintos_cobertura_min: 0.5,
  dwg_recinto_lado_min_esp: 7,
  dwg_recinto_compacidad_sliver: 0.25,
  dwg_recinto_compacidad_fina: 0.45,
  dwg_recinto_snap_esp: 1.6,
  dwg_cota_asociacion_m: 0.05,
  dwg_cota_discrepancia_m: 0.01,
};

/** Rango válido de cada parámetro (mismo criterio en el esquema de la API y en la validación del panel). */
export const RANGO_PARAMETROS_MEDICION_DWG: Readonly<Record<keyof ParametrosMedicionDwg, readonly [number, number]>> = {
  dwg_resolucion_m: [0.00001, 0.05],
  dwg_aplanado_m: [0.0001, 0.1],
  dwg_muro_espesor_min_m: [0.001, 0.5],
  dwg_muro_espesor_max_m: [0.05, 5],
  dwg_muro_espesor_paso_m: [0.001, 0.5],
  dwg_muro_pares_min: [1, 100],
  dwg_muro_conf_min: [0, 1],
  dwg_snap_min_m: [0.001, 0.5],
  dwg_snap_max_m: [0.01, 5],
  dwg_hueco_min_m: [0.001, 1],
  dwg_vano_fino_max_m: [0.3, 5],
  dwg_extension_min_m: [0.001, 10],
  dwg_extension_max_m: [10, 1_000_000],
  dwg_cota_tipica_min_m: [0.0001, 1],
  dwg_cota_tipica_max_m: [1, 100_000],
  dwg_puerta_ancho_min_m: [0.1, 2],
  dwg_puerta_ancho_max_m: [0.5, 5],
  dwg_puerta_tolerancia: [0.001, 0.5],
  dwg_rotulo_tolerancia: [0.001, 0.2],
  dwg_vista_union_m: [0.001, 50],
  dwg_vista_entidades_min: [1, 1000],
  dwg_altura_entrepiso_min_m: [0.5, 10],
  dwg_altura_entrepiso_max_m: [1, 50],
  dwg_muro_conf_alta: [0, 1],
  dwg_muro_familia_peso_min: [0, 1],
  dwg_superficie_ratio_max: [0.1, 100],
  dwg_recintos_cobertura_min: [0, 1],
  dwg_recinto_lado_min_esp: [1, 100],
  dwg_recinto_compacidad_sliver: [0, 1],
  dwg_recinto_compacidad_fina: [0, 1],
  dwg_recinto_snap_esp: [1, 5],
  dwg_cota_asociacion_m: [0.001, 1],
  dwg_cota_discrepancia_m: [0.0001, 1],
};

/**
 * Parejas (mínimo, máximo) que tienen que quedar en orden. Las valida el panel antes de publicar;
 * el script además se defiende (un rango invertido no opina).
 */
export const PARES_MIN_MAX_MEDICION_DWG: readonly (readonly [keyof ParametrosMedicionDwg, keyof ParametrosMedicionDwg])[] = [
  ['dwg_muro_espesor_min_m', 'dwg_muro_espesor_max_m'],
  ['dwg_snap_min_m', 'dwg_snap_max_m'],
  ['dwg_extension_min_m', 'dwg_extension_max_m'],
  ['dwg_cota_tipica_min_m', 'dwg_cota_tipica_max_m'],
  ['dwg_puerta_ancho_min_m', 'dwg_puerta_ancho_max_m'],
  ['dwg_altura_entrepiso_min_m', 'dwg_altura_entrepiso_max_m'],
  ['dwg_recinto_compacidad_sliver', 'dwg_recinto_compacidad_fina'],
  ['dwg_muro_conf_min', 'dwg_muro_conf_alta'],
];

/** Etiqueta y pista de cada parámetro para el panel de Calibración (español-MX, staff). */
export const DESCRIPCION_PARAMETROS_MEDICION_DWG: Readonly<
  Record<keyof ParametrosMedicionDwg, { etiqueta: string; pista: string; paso: number }>
> = {
  dwg_resolucion_m: { etiqueta: 'Resolución de coordenadas (m)', pista: 'Los vértices se redondean a esta distancia antes de medir', paso: 0.0005 },
  dwg_aplanado_m: { etiqueta: 'Aplanado de curvas (m)', pista: 'Flecha máxima entre un arco y los tramos rectos que lo reemplazan', paso: 0.001 },
  dwg_muro_espesor_min_m: { etiqueta: 'Espesor de muro mínimo (m)', pista: 'Dos caras más juntas que esto son líneas repetidas, no un muro', paso: 0.005 },
  dwg_muro_espesor_max_m: { etiqueta: 'Espesor de muro máximo (m)', pista: 'Dos caras más separadas que esto no se emparejan como un muro', paso: 0.05 },
  dwg_muro_espesor_paso_m: { etiqueta: 'Clase del espesor (m)', pista: 'Ancho con que se agrupan las separaciones para hallar el espesor dominante', paso: 0.005 },
  dwg_muro_pares_min: { etiqueta: 'Pares mínimos de doble trazo', pista: 'Menos pares de caras que esto: la capa no se mide como muro ni opina en la escala', paso: 1 },
  dwg_muro_conf_min: { etiqueta: 'Nitidez mínima del doble trazo', pista: 'Fracción 0–1 de pares en el espesor dominante para que la capa cuente como muro', paso: 0.05 },
  dwg_snap_min_m: { etiqueta: 'Cierre de recintos mínimo (m)', pista: 'Tolerancia mínima para fundir extremos casi coincidentes', paso: 0.005 },
  dwg_snap_max_m: { etiqueta: 'Cierre de recintos máximo (m)', pista: 'Tolerancia máxima para fundir extremos', paso: 0.05 },
  dwg_hueco_min_m: { etiqueta: 'Hueco mínimo de vano (m)', pista: 'Un corte más corto que esto en un muro no se trata como vano', paso: 0.01 },
  dwg_vano_fino_max_m: { etiqueta: 'Vano puenteado máximo (m)', pista: 'Un cuarto cerrado con un vano más ancho queda como aproximado', paso: 0.05 },
  dwg_extension_min_m: { etiqueta: 'Tamaño mínimo del dibujo (m)', pista: 'Con una unidad que deja el dibujo más chico que esto, esa unidad se descarta', paso: 0.01 },
  dwg_extension_max_m: { etiqueta: 'Tamaño máximo del dibujo (m)', pista: 'Con una unidad que deja el dibujo más grande que esto, esa unidad se descarta', paso: 100 },
  dwg_cota_tipica_min_m: { etiqueta: 'Cota típica mínima (m)', pista: 'Mediana de las cotas: por debajo, la unidad candidata se descarta', paso: 0.001 },
  dwg_cota_tipica_max_m: { etiqueta: 'Cota típica máxima (m)', pista: 'Mediana de las cotas: por encima, la unidad candidata se descarta', paso: 10 },
  dwg_puerta_ancho_min_m: { etiqueta: 'Ancho de puerta mínimo (m)', pista: 'Bloques con hoja y abatimiento medidos por su geometría, no por su nombre', paso: 0.05 },
  dwg_puerta_ancho_max_m: { etiqueta: 'Ancho de puerta máximo (m)', pista: 'Bloques con hoja y abatimiento medidos por su geometría, no por su nombre', paso: 0.05 },
  dwg_puerta_tolerancia: { etiqueta: 'Tolerancia del abatimiento', pista: 'Fracción 0–1: cuánto puede diferir la hoja del radio del arco', paso: 0.01 },
  dwg_rotulo_tolerancia: { etiqueta: 'Tolerancia del rótulo de cota', pista: 'Fracción 0–1: cuánto puede diferir rótulo/geometría de una potencia de 10', paso: 0.005 },
  dwg_vista_union_m: { etiqueta: 'Separación entre vistas (m)', pista: 'Trazos más cerca que esto son de la misma vista; más lejos, de otra lámina', paso: 0.05 },
  dwg_vista_entidades_min: { etiqueta: 'Vista mínima (entidades)', pista: 'Una isla más chica (un símbolo, un eje suelto) se suma a la vista más cercana', paso: 1 },
  dwg_altura_entrepiso_min_m: { etiqueta: 'Altura de entrepiso mínima (m)', pista: 'Cotas verticales de elevaciones y cortes más chicas son losas, firmes o pretiles', paso: 0.1 },
  dwg_altura_entrepiso_max_m: { etiqueta: 'Altura de entrepiso máxima (m)', pista: 'Cotas verticales más grandes son totales o dobles alturas', paso: 0.1 },
  dwg_muro_conf_alta: { etiqueta: 'Nitidez del doble trazo "medida"', pista: 'Fracción 0–1: por debajo, el largo del muro sale como aproximado', paso: 0.05 },
  dwg_muro_familia_peso_min: { etiqueta: 'Peso mínimo de una familia de espesor', pista: 'Fracción 0–1 del largo emparejado de la capa: pares de otra separación (muebles, jambas) no suman al muro', paso: 0.01 },
  dwg_superficie_ratio_max: { etiqueta: 'Superficie tomada por muro (veces)', pista: 'Área encerrada / (eje × espesor) arriba de esto, con poca nitidez, es un piso y no un muro', paso: 0.1 },
  dwg_recintos_cobertura_min: { etiqueta: 'Cobertura mínima de recintos', pista: 'Fracción 0–1 del trazo que tiene que cerrar para ofrecer el área aproximada', paso: 0.05 },
  dwg_recinto_lado_min_esp: { etiqueta: 'Recinto mínimo (espesores)', pista: 'Lado mínimo de un cuarto, en espesores de muro: descarta columnas y ductos', paso: 0.5 },
  dwg_recinto_compacidad_sliver: { etiqueta: 'Compacidad de una tira', pista: 'Fracción 0–1: por debajo, la cara cerrada es una tira y no un cuarto', paso: 0.05 },
  dwg_recinto_compacidad_fina: { etiqueta: 'Compacidad de un cuarto', pista: 'Fracción 0–1: desde acá la forma es de cuarto y su área se da como medida', paso: 0.05 },
  dwg_recinto_snap_esp: { etiqueta: 'Fusión al cerrar cuartos (espesores)', pista: 'Funde las dos caras del muro en un eje para cerrar los cuartos', paso: 0.1 },
  dwg_cota_asociacion_m: { etiqueta: 'Cota pegada a su trazo (m)', pista: 'Distancia máxima entre el origen de una cota y la línea que mide', paso: 0.01 },
  dwg_cota_discrepancia_m: { etiqueta: 'Cota que no cuadra (m)', pista: 'Diferencia entre lo escrito en la cota y lo que mide su dibujo a partir de la cual se avisa', paso: 0.005 },
};

/**
 * Completa y sanea lo que venga (calibración guardada): cada clave ausente, no numérica o fuera de
 * rango cae al default. Nunca tira. Es lo que el worker le pasa al script.
 */
export function normalizarParametrosMedicionDwg(x: unknown): ParametrosMedicionDwg {
  const src = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
  const out = { ...PARAMETROS_MEDICION_DWG_DEFAULT };
  for (const k of Object.keys(out) as (keyof ParametrosMedicionDwg)[]) {
    const v = src[k];
    const [mn, mx] = RANGO_PARAMETROS_MEDICION_DWG[k];
    if (typeof v === 'number' && Number.isFinite(v) && v >= mn && v <= mx) out[k] = v;
  }
  return out;
}
